import React, { useMemo, useCallback, useEffect, useContext } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from './CartReviewTile'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import moment from 'moment'
import CartReviewPriceStack from './CartReviewPriceStack'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Divider from 'LuxKit/Divider'
import { useUserCartItemState } from 'hooks/UserCart/useUserCartItemState'
import FlightCartReviewTileExtension from './FlightCartReviewTileExtension'
import { FlightViewTypes } from 'constants/flight'
import styled from 'styled-components'
import useRefreshFlightItem from 'hooks/UserCart/useRefreshFlightItem'
import Group from 'components/utils/Group'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { buildStandaloneFlightSearchUrl, getFlightLoyaltyProductType, getFlightLoyaltyProductTypeRank, getFlightsTotal } from 'lib/flights/flightUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { userCartItemsDelete, userCartItemsSaveForLater } from 'actions/UserCartActions'
import { getUserCartItemsArray } from 'selectors/userCart'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import ExpiryTimer from './ExpiryTimer'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'
import { rem } from 'polished'
import { multiCartDeleteItemEvent, multiCartSaveItemForLaterEvent } from 'analytics/snowplow/events'
import GeoContext from 'contexts/geoContext'
import { arrayToMap, sortBy } from 'lib/array/arrayUtils'
import { isTwoOneWaysItem } from './CartReviewUtils'
import CartReviewTileContent from './CartReviewTileContent'
import CondensedCartReviewTile from './CondensedCartReviewTile'
import LoadingCartReviewTileSkeleton from './LoadingCartReviewTileSkeleton'
import DeletedCartReviewTile from './DeletedCartReviewTile'
import CartReviewTileActions from './CartReviewTileActions'
import getLoyaltyEarnKeyFromFlightItems from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKeyFromFlightItems'

const ExtensionContainer = styled(Group)`
  grid-area: extension;
`

const DividerPadding = styled.div`
  padding-inline: ${rem(20)};
`

interface Props {
  item: App.Checkout.FlightItem
  tileVariant?: 'default' | 'condensed'
  hideActionButtons?: boolean
  hidePrices?: boolean
  hidePassengerCount?: boolean
  hideLoyaltyPoints?: boolean
}

function FlightCartReviewTile({ item, tileVariant = 'default', hideActionButtons = false, hidePrices = false, hidePassengerCount = false, hideLoyaltyPoints = false }: Props) {
  const dispatch = useAppDispatch()
  const { deleted, saveForLater } = useUserCartItemState(item.itemId)
  const cartItems = useAppSelector(getUserCartItemsArray)
  const flightItems = cartItems.filter(cartItem => isFlightItem(cartItem))
  const cartState = useAppSelector(state => state.auth.userCart.itemStates)
  const { dataStatus, initiateRefresh } = useRefreshFlightItem(item)
  const failedRefresh = dataStatus === 'refreshFailed'
  const isPast = dateIsBeforeOrSame(new Date(item.flights[0].departingDate), new Date())
  const hasExpired = dataStatus === 'expired'
  const isAvailable = !failedRefresh && !hasExpired && !isPast
  const isBookable = !deleted && isAvailable
  const canShowExtensionAndTimer = !!item.expiry && dataStatus !== 'refreshFailed'
  const flightSearchUrl = useMemo(() => buildStandaloneFlightSearchUrl({
    originAirportCode: item.originAirportCode,
    originAirportName: item.flights[0].departingAirportName,
    destinationAirportCode: item.destinationAirportCode,
    destinationAirportName: item.flights[0].arrivalAirportName,
    departDate: item.flights[0].departingDate,
    returnDate: item.flights.length > 1 ? item.flights[1].departingDate : null,
    adults: item.occupants.adults,
    children: item.occupants.children ?? 0,
    infants: item.occupants.infants ?? 0,
    childrenAge: item.occupants.childrenAge?.join(',') ?? '',
    fareClass: item.flights[0].fareFamily?.cabin?.toLowerCase() ?? 'economy',
    fareType: item.fareType,
  }), [
    item.originAirportCode,
    item.flights,
    item.destinationAirportCode,
    item.occupants,
    item.fareType,
  ])
  const { airports } = useContext(GeoContext)

  const hasTwoOneWaysFlightsInUserCart = useMemo(() => {
    const activeItems = flightItems.filter(flightItem => !cartState[flightItem.itemId]?.saveForLater)
    if (!activeItems.length) return false

    const twoOneWaysFlights = activeItems.find(item => item.viewType === FlightViewTypes.TWO_ONE_WAYS)
    if (!twoOneWaysFlights) return false

    return isTwoOneWaysItem(twoOneWaysFlights)
  }, [flightItems, cartState])

  const isTwoOneWaysAndUserCartContainsFlights = useMemo(() => {
    const activeItems = flightItems.filter(flightItem => !cartState[flightItem.itemId]?.saveForLater)
    if (!activeItems.length) return false

    return !!activeItems.length && isTwoOneWaysItem(item)
  }, [cartState, flightItems, item])

  useEffect(() => {
    if (!isAvailable && !saveForLater && !deleted) {
      dispatch(userCartItemsSaveForLater([item.itemId], true))
    }
  }, [isAvailable, saveForLater, dispatch, item.itemId, deleted])

  const formattedOccupancy = useMemo(() => {
    if (hidePassengerCount) return ''

    const hasInfants = item.occupants.infants && item.occupants.infants > 0
    const formattedOccupants = formatOccupantsShort(item.occupants)
    return `${formattedOccupants} ${hasInfants ? '(on lap)' : ''}`
  }, [item.occupants, hidePassengerCount])

  const title = useMemo(() => {
    switch (item.viewType) {
      case FlightViewTypes.ONE_WAY:
        return 'One-way flight'
      case FlightViewTypes.TWO_ONE_WAYS_AND_RETURN:
      case FlightViewTypes.TWO_ONE_WAYS:
        return 'Return flights'
      case FlightViewTypes.MULTI_CITY:
        return 'Multi-city flights'
      default:
        return ''
    }
  }, [item.viewType])

  const dates = useMemo(() => {
    const firstFlight = item.flights[0]
    if (!firstFlight.departingDate || !firstFlight.departingAirportName || !firstFlight.arrivalAirportName) {
      return ''
    }

    const date = moment(firstFlight.departingDate).format('D MMM YYYY')

    switch (item.viewType) {
      case FlightViewTypes.ONE_WAY:
        return `${date} • ${firstFlight.departingAirportName} to ${firstFlight.arrivalAirportName}`
      case FlightViewTypes.TWO_ONE_WAYS_AND_RETURN:
      case FlightViewTypes.TWO_ONE_WAYS:
      case FlightViewTypes.MULTI_CITY: {
        const lastFlight = item.flights[item.flights.length - 1]
        if (!lastFlight.departingDate || !lastFlight.arrivalAirportName) {
          return ''
        }

        const endDate = moment(lastFlight.departingDate).format('D MMM YYYY')
        const fromCity = firstFlight.departingAirportName
        const toCity = item.viewType === FlightViewTypes.MULTI_CITY ? lastFlight.arrivalAirportName : firstFlight.arrivalAirportName

        return `${date} - ${endDate} • ${fromCity} to ${toCity}`
      }
      default:
        return ''
    }
  }, [item.flights, item.viewType])

  const totalCost = useMemo(() => getFlightsTotal(item), [item])

  const bannerLabelVariant = useMemo(() => {
    if (isPast) return 'pastDates'
    if (hasExpired) return 'expired'
    if (failedRefresh) return 'failedRefresh'
    if (isBookable && saveForLater && hasTwoOneWaysFlightsInUserCart) return 'bookingLimit'
    if (saveForLater && isTwoOneWaysAndUserCartContainsFlights) return 'bookingLimit'
    return null
  }, [isPast, hasExpired, failedRefresh, isBookable, saveForLater, hasTwoOneWaysFlightsInUserCart, isTwoOneWaysAndUserCartContainsFlights])

  const bannerContent = useMemo(() => {
    switch (bannerLabelVariant) {
      case 'pastDates':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">Your selected dates are in the past.</BodyText>
            <TextLink weight="bold" to="/flights" target="_blank">
              Check availability
            </TextLink>
          </Group>
        )
      case 'expired':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">This flight price has expired</BodyText>
            <TextLink weight="bold" onClick={initiateRefresh}>
              Refresh search
            </TextLink>
          </Group>
        )
      case 'failedRefresh':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">We couldn't refresh your flight.</BodyText>
            <TextLink weight="bold" to={flightSearchUrl} target="_blank">
              Search for flights
            </TextLink>
          </Group>
        )
      case 'bookingLimit':
        return 'This flight needs to be booked separately from your current selection'
      default:
        return ''
    }
  }, [bannerLabelVariant, initiateRefresh, flightSearchUrl])

  const banner = useMemo(() => {
    if (!bannerLabelVariant) return null

    return (
      <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
        {bannerContent}
      </MessageBanner>
    )
  }, [bannerLabelVariant, bannerContent])

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartSaveItemForLaterEvent(item, saveForLater)

    if (saveForLater) {
      // First make this flight active
      dispatch(userCartItemsSaveForLater([item.itemId]))
      // Then deactivate all other flights in case there are two one-way items or the new item is a two one-way
      if (hasTwoOneWaysFlightsInUserCart || isTwoOneWaysItem(item)) {
        flightItems.forEach(flightItem => {
          if (flightItem.itemId !== item.itemId && !cartState[flightItem.itemId]?.saveForLater) {
            dispatch(userCartItemsSaveForLater([flightItem.itemId]))
          }
        })
      }
    } else {
      dispatch(userCartItemsSaveForLater([item.itemId]))
    }
  }, [item, saveForLater, dispatch, hasTwoOneWaysFlightsInUserCart, flightItems, cartState])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartDeleteItemEvent(item)
    dispatch(userCartItemsDelete([item.itemId]))
  }, [dispatch, item])

  const extension = useMemo(() => {
    if (!canShowExtensionAndTimer) return null

    return (
      <ExtensionContainer direction="vertical" gap={8}>
        <DividerPadding><Divider kind="primary" /></DividerPadding>
        {item.flights.map((flight, index) => (
          <React.Fragment key={flight.journeyId}>
            <FlightCartReviewTileExtension flight={flight} />
            {index < item.flights.length - 1 && <DividerPadding><Divider kind="primary" /></DividerPadding>}
          </React.Fragment>
        ))}
      </ExtensionContainer>
    )
  }, [canShowExtensionAndTimer, item.flights])

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    const airportsByCode = arrayToMap(airports, airport => airport.code)

    const loyaltyProductTypes = item.flights.map(flight => {
      const departingAirport = airportsByCode.get(flight.departingAirportCode)
      const arrivalAirport = airportsByCode.get(flight.arrivalAirportCode)

      const isDomestic = departingAirport?.countryCode === arrivalAirport?.countryCode
      return getFlightLoyaltyProductType(flight.fareFamily?.cabin?.toLowerCase() ?? 'economy', isDomestic)
    })

    const rankedLoyaltyProductTypes = sortBy(loyaltyProductTypes, productType => getFlightLoyaltyProductTypeRank(productType), 'asc')
    const highestRankedProductType = rankedLoyaltyProductTypes[0]

    const earnKey = getLoyaltyEarnKeyFromFlightItems(item.flights, highestRankedProductType, airports)

    return {
      calculationRequests: [{
        loyaltyProductType: highestRankedProductType,
        price: totalCost,
        earnKey,
        offerId: '',
        packageId: '',
      }],
      calculationType: 'exact',
    }
  }, [airports, item.flights, totalCost])

  if (dataStatus === 'refreshing') {
    return <LoadingCartReviewTileSkeleton />
  }

  if (deleted) {
    return <DeletedCartReviewTile title={title} itemIds={item.itemId} />
  }

  if (tileVariant === 'condensed') {
    return <CondensedCartReviewTile
      label={<ExpiryTimer
        canShowExtension={canShowExtensionAndTimer}
        expiry={item.expiry ?? 0}
      />}
      banner={banner}
      content={<CartReviewTileContent
        title={title}
        dates={dates}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
      />}
      onDelete={hideActionButtons ? undefined : handleDelete}
      price={<CartReviewPriceStack
        price={totalCost}
        value={totalCost}
      />}
    />
  }
  const actions = hideActionButtons ? undefined : <CartReviewTileActions
    isBookable={isBookable}
    saveForLater={saveForLater}
    onSaveForLater={handleSaveForLater}
    onDelete={handleDelete}
  />
  const price = hidePrices ? null : <CartReviewPriceStack
    price={totalCost}
    value={totalCost}
  />

  return <CartReviewTile
    label={<ExpiryTimer
      canShowExtension={canShowExtensionAndTimer}
      expiry={item.expiry ?? 0}
    />}
    content={<CartReviewTileContent
      title={title}
      dates={dates}
      occupancy={formattedOccupancy}
      loyaltyPointsEarningConfiguration={hideLoyaltyPoints ? undefined : loyaltyPointsEarningConfiguration}
    />}
    price={price}
    banner={banner}
    extension={extension}
    isBookable={isBookable}
    saveForLater={saveForLater}
    actions={actions}
  />
}

export default FlightCartReviewTile
